import { useTranslation } from 'react-i18next'
import type { NoticeKind } from '@gk/contracts'
import type { AreaRow } from '../data/geo.ts'
import { noticeLineText, type NoticeShown } from '../data/courseStatus.ts'

/** The one line of a course notice (M16-B1): "Compétition · Rouge · Coupe du club · départ 8 h". The same component on Today's first card (M16-A), the
 *  day board's header (Z), the week grid's header (F), the shed screen and the management page; the text is `noticeLineText` (data/courseStatus.ts).
 *  A tone per kind, each with its night colours (theme tokens: flag for a closure, sand for a restriction, brand for a competition); `size="lg"` is the
 *  shed screen's (large type on its dark background). Nothing to tap: a line is a fact about the day. */
const TONES: Record<NoticeKind, { md: string; border: string }> = {
  closure: { md: 'bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-100', border: 'border-flag dark:border-red-400' },
  restriction: { md: 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100', border: 'border-amber-500 dark:border-amber-400' },
  event: { md: 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100', border: 'border-brand dark:border-emerald-400' },
  maintenance: { md: 'bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-100', border: 'border-sky-500 dark:border-sky-400' },
  pins: { md: 'bg-fairway/15 text-slate-900 dark:bg-fairway/20 dark:text-slate-50', border: 'border-fairway' },
  info: { md: 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100', border: 'border-slate-400 dark:border-slate-500' },
}

export function NoticeLine({ notice, areaById, lang, size = 'md', testId }: {
  notice: NoticeShown; areaById: Map<string, AreaRow>; lang: string; size?: 'md' | 'lg'; testId?: string
}) {
  const { t } = useTranslation()
  const tone = TONES[notice.kind] ?? TONES.info
  const cls = size === 'lg'
    ? `rounded-2xl border-l-8 bg-slate-800 px-5 py-4 text-3xl font-semibold text-white ${tone.border}`
    : `rounded-xl border-l-4 px-3 py-2 text-base font-medium leading-snug ${tone.md} ${tone.border}`
  return <p className={cls} data-testid={testId ?? 'notice-line'} data-kind={notice.kind} data-notice-id={notice.id}>{noticeLineText(notice, areaById, lang, t)}</p>
}
